import { Check, ExternalLink, Pencil } from "lucide-react";
import { Label } from "@/components/ui/label";
import { btn } from "@/components/common/btn";
import { shortDate } from "@/lib/format";
import { WODatesRow } from "./WODatesRow";
import { WOFlagsBar, PoNumberField, RetainageField, ProjectTypeField, TaxNoField, TaxRateField, HideTaxField, custOverride } from "./WOFlagsBar";
import { WOSourceProposalBadge } from "./WOSourceProposalBadge";
import { CustomerNotesPanel } from "@/components/CustomerNotesPanel";

export const InfoCard = ({ title, action, children, testId, className = "" }) => (
  <section className={`rounded-xl border bg-card p-4 space-y-3 ${className}`} data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

export const InfoRow = ({ label, children, testId }) => (
  <div className="grid grid-cols-[120px_1fr] gap-2 text-sm items-start" data-testid={testId}>
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 min-w-0 break-words">{children ?? "—"}</span>
  </div>
);

// Small label-over-value cell, shared by the dates row and the contract fields so they line up.
const Cell = ({ label, htmlFor, children, testId }) => (
  <div className="min-w-0">
    <Label htmlFor={htmlFor} className="block text-xs font-normal text-slate-500 leading-5">{label}</Label>
    <div className="text-sm" data-testid={testId}>{children}</div>
  </div>
);

const Flag = ({ on, label, testId }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${on ? "border-emerald-500 text-emerald-600" : "border-slate-200 text-slate-400"}`} data-testid={testId}>
    {on && <Check className="w-3 h-3" />}{label}
  </span>
);

// "General" card on the Info tab — read view (Edit swaps in the dates/site inputs + flag checkboxes).
// Contract fields (Customer PO #, project type, retainage, tax no / rate) sit inline; every pill is on the last row.
export function WOGeneralCard({
  form, setForm, isEditable, editing, setEditing,
  clientSites, clientId, clientName, navigate, editingWO, setEditingWO,
  scheduledDateRef, editingCompletionDate, setEditingCompletionDate,
  subtotal, taxAmount, total, loadWorkOrders, siteOptional,
  custTaxOverride, taxOverride, openTaxOverride, closeTaxOverride, setTaxOverride,
}) {
  const site = clientSites.find((s) => s.id === form.site_id);
  const addrParts = site ? [site.address, site.city, site.state, site.zip].filter(Boolean) : [];
  const addr = addrParts.join(", ");
  const mapsUrl = addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : "";
  const canEdit = form.status !== "completed" && form.status !== "cancelled";

  return (
    <InfoCard title="General" testId="wo-general-card" className="h-full"
      action={canEdit && (
        <button type="button" className={btn(editing ? "write" : "nav")} onClick={() => setEditing(!editing)} data-testid="wo-general-edit">
          {editing ? <><Check className="w-3 h-3" /> Done</> : <><Pencil className="w-3 h-3" /> Edit</>}
        </button>
      )}>
      <WOSourceProposalBadge form={form} editingWO={editingWO} clientId={clientId} navigate={navigate} />
      {editing ? (
        <div className="space-y-3">
          <WODatesRow
            stacked
            form={form} setForm={setForm} isEditable={isEditable} clientSites={clientSites}
            scheduledDateRef={scheduledDateRef} editingCompletionDate={editingCompletionDate} setEditingCompletionDate={setEditingCompletionDate}
            editingWO={editingWO} setEditingWO={setEditingWO} clientId={clientId}
            subtotal={subtotal} taxAmount={taxAmount} total={total} loadWorkOrders={loadWorkOrders} siteOptional={siteOptional}
          />
          <WOFlagsBar form={form} setForm={setForm} isEditable={isEditable} section="flags" />
        </div>
      ) : (
        <div className="space-y-2" data-testid="wo-general-read">
          <InfoRow label="Name" testId="wo-info-title">{form.title}</InfoRow>
          <InfoRow label="WO #"><span className="font-mono">{form.wo_number || "Auto"}</span></InfoRow>
          <div className="grid grid-cols-3 gap-2 items-start">
            <div className="col-span-2">
              <InfoRow label="Customer">
                {clientName ? (
                  <button type="button" className="text-brand hover:underline text-left" onClick={() => navigate("/clients", { state: { openClientId: clientId } })} data-testid="wo-info-customer-link">{clientName}</button>
                ) : "—"}
              </InfoRow>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-2 items-center text-sm">
              <Label htmlFor="customer-po-number" className="text-sm font-normal text-slate-500 whitespace-nowrap">PO #</Label>
              <PoNumberField form={form} setForm={setForm} className="h-7 w-full" />
            </div>
          </div>
          <InfoRow label="Site" testId="wo-info-site">{site?.name}</InfoRow>
          <InfoRow label="Address">
            {addr ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline inline-flex items-center gap-1" data-testid="wo-site-address-link">{addr}<ExternalLink className="w-3 h-3" /></a> : "—"}
          </InfoRow>
          <div className="grid grid-cols-3 gap-2 pt-1">
            <Cell label="Date" testId="wo-info-date"><span className="tabular-nums">{shortDate(form.work_date)}</span></Cell>
            <Cell label="Scheduled" testId="wo-info-scheduled"><span className="tabular-nums">{shortDate(form.scheduled_date)}</span></Cell>
            <Cell label="Completion" testId="wo-info-completion"><span className="tabular-nums">{shortDate(form.completion_date)}</span></Cell>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="wo-contract-record">
            <Cell label="Project Type"><ProjectTypeField form={form} setForm={setForm} className="h-7 w-full" /></Cell>
            <Cell label="Retainage %" htmlFor="retainage-percent"><RetainageField form={form} setForm={setForm} className="h-7 w-full" /></Cell>
            <Cell label="Tax No" htmlFor="wo-tax-authority">
              <div className="flex items-center gap-2">
                <TaxNoField form={form} setForm={setForm} isEditable={canEdit} className="h-7 w-20" />
                <TaxRateField form={form} setForm={setForm} taxOverride={taxOverride} openTaxOverride={openTaxOverride} closeTaxOverride={closeTaxOverride} setTaxOverride={setTaxOverride} />
              </div>
            </Cell>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="wo-info-flags">
            <Flag on={!!form.annual_service} label="Annual Service" testId="wo-flag-annual" />
            <Flag on={!!form.show_breakdown} label="Breakout" testId="wo-flag-breakout" />
            <Flag on={form.lift_required ?? true} label="Lift" testId="wo-flag-lift" />
            <Flag on={!!form.dot_inspection_required} label="DOT Inspection" testId="wo-flag-dot" />
            <HideTaxField form={form} setForm={setForm} woId={editingWO?.id} hiddenTaxAmount={taxAmount} custTaxOverride={custOverride(form, custTaxOverride)} />
            {form.field_complete && form.status !== "completed" && <Flag on label="Field Complete" testId="badge-wo-field-complete" />}
          </div>
        </div>
      )}
      {/* Customer notes — live from the customer record (iter241) */}
      <CustomerNotesPanel clientId={clientId} />
    </InfoCard>
  );
}
